/**
 * SDA-299: "Website visits" card on the admin studio page — which public pages
 * the studio opened (via a campaign link or while logged in), newest first.
 */

import { useEffect, useState } from 'react';
import { getAdminStudioPageViews, type StudioPageView } from '../../../api/admin';

const PAGE_LABELS: Record<string, string> = {
  home: 'Home',
  event_search: 'Event search',
  event_detail: 'Event page',
  booking: 'Booking',
  checkout: 'Checkout',
  checkout_confirmation: 'Booking confirmed',
  inquiry_form: 'Inquiry form',
  register: 'Sign-up',
  register_success: 'Sign-up done',
  activate: 'Account activation',
  studio_dashboard: 'Studio dashboard',
};

export function pageLabel(pageType: string): string {
  return PAGE_LABELS[pageType] ?? pageType;
}

/** e.g. "Oct 28, 2026, 3:04 PM ET" — always Eastern, whatever the admin's browser zone. */
export function formatVisitTimeET(iso: string): string {
  return `${new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} ET`;
}

export default function WebsiteVisitsCard({ studioId }: { studioId: string }) {
  const [visits, setVisits] = useState<StudioPageView[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setVisits(null);
    setFailed(false);
    getAdminStudioPageViews(studioId)
      .then((rows) => { if (!cancelled) setVisits(rows); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [studioId]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
      <h2 className="text-lg font-semibold text-warm-900 mb-4">Website visits</h2>
      {failed ? (
        <p className="text-warm-500 text-sm">Couldn't load website visits.</p>
      ) : visits === null ? (
        <p className="text-warm-400 text-sm">Loading…</p>
      ) : visits.length === 0 ? (
        <p className="text-warm-500 text-sm">No tracked visits yet.</p>
      ) : (
        <ul className="space-y-3 max-h-96 overflow-y-auto">
          {visits.map((v) => {
            const where = v.show_name
              ? [v.show_name, v.city_name].filter(Boolean).join(' – ')
              : null;
            return (
              <li key={v.id} className="text-sm">
                <p className="text-warm-900 font-medium">{pageLabel(v.page_type)}</p>
                {where && <p className="text-warm-700">{where}</p>}
                <p className="text-warm-500 text-xs">{formatVisitTimeET(v.created_at)}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
